import { CurrencyCode } from "@secureo/common/typings/Forex"
import getCustomFieldsByName from "@secureo/common/utils/commerceTools/getCustomFieldsByName"
import parseLineItemUuidCustomField from "@secureo/common/utils/commerceTools/parseLineItemUuidCustomField"

import {
	shippingZones,
	shippingZoneCostsByZoneNumber,
	flatFeesByCurrencyCode,
	ShippingZoneCosts,
	ShippingCostsByLineItemId,
	shippingCostMultiplierByCurrencyCode,
} from "../../config/shippingZonesAndCosts"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import {
	MinimalLineItemForTaxCalculation,
	MinimalCartForTaxCalculation,
} from "@secureo/common/utils/api/cart/fetchCartByCartId"

// ShippingZone 0 (aka. ShopCountry/Instance)
// Apply no lineItem shippingCosts
// Apply flatFeeForSmallAmount (7,50€) if order < 75€

// Other shippingZone
// Apply lineItem shippingCosts to products with haulageDelivery
// Apply flatFeeForSmallAmount (7,50€) if order < 75€
// Apply 1 x flatFeeForNonHaulageDelivery (14,90€) if at least 1 product is !haulageDelivery

const formatCartLineItems = (lineItems: MinimalLineItemForTaxCalculation[]) =>
	lineItems.map((lineItem) => {
		const {
			productId,
			quantity,
			variant: { attributesRaw },
			custom,
		} = lineItem

		const isHaulageDeliveryAttribute = attributesRaw.find(
			({ name }) => name === "isHaulageDelivery",
		)
		const isHaulageDelivery: boolean =
			(isHaulageDeliveryAttribute && (isHaulageDeliveryAttribute.value as any)) || false

		const { uuid } = getCustomFieldsByName(custom)
		const { configurationForProductId } = parseLineItemUuidCustomField(productId, uuid)
		const isConfiguration = !!(
			configurationForProductId && configurationForProductId !== productId
		)

		const weightKgAttribute = attributesRaw.find(({ name }) => name === "weightKg")

		const weightKg: number = (weightKgAttribute && (weightKgAttribute.value as any)) || 0

		return {
			...lineItem,
			isHaulageDelivery,
			weightKg,
			quantity,
			isConfiguration,
		}
	})

export const getShippingCostsCents = (
	lineItemWeightKg: number,
	quantity: number,
	shippingZoneCosts: ShippingZoneCosts,
	currencyCode: CurrencyCode,
) => {
	const shippingCostsGreaterThanLineItemWeight = Object.entries(shippingZoneCosts)
		.filter(([weightThresholdKg]) => Number(weightThresholdKg) >= lineItemWeightKg)
		.sort((a, b) => {
			const [weightThresholdKgA] = a
			const [weightThresholdKgB] = b

			return Number(weightThresholdKgA) - Number(weightThresholdKgB)
		})

	const [applicableShippingClass] = shippingCostsGreaterThanLineItemWeight
	const [, shippingCostsCents] = applicableShippingClass

	const shippingCostMultiplier = shippingCostMultiplierByCurrencyCode[currencyCode]

	return shippingCostsCents * quantity * shippingCostMultiplier
}

const getCartShippingCosts = (
	cart: MinimalCartForTaxCalculation,
	shippingCountryCode: CountryCode,
): {
	flatFeeForSmallAmount: number
	flatFeeForNonHaulageDelivery: number
	shippingCostsByLineItemId: ShippingCostsByLineItemId
} => {
	const {
		lineItems,
		taxedPrice: {
			totalGross: { centAmount: totalGrossCents },
		},
		totalPrice: { currencyCode },
	} = cart

	const shippingZone = shippingZones[shippingCountryCode]
	const shippingZoneCosts = shippingZoneCostsByZoneNumber[shippingZone]

	const isFreeShippingZone =
		shippingZone === 0 || (currencyCode === "PLN" && shippingCountryCode === "PL") // Handle edge case for PL in pl-PL locale (uses PLN currencyCode as a workaround)

	const formattedLineItems = formatCartLineItems(lineItems)

	const lineItemsWithHaulageDelivery = formattedLineItems.filter(
		({ isHaulageDelivery }) => isHaulageDelivery,
	)
	const lineItemsWithoutConfigurations = formattedLineItems.filter(
		({ isConfiguration }) => !isConfiguration,
	)
	const allNonConfigurationProductsHaveHaulageDelivery = lineItemsWithoutConfigurations.every(
		({ isHaulageDelivery }) => isHaulageDelivery,
	)

	// Calculate if flat fees need to be applied
	const isApplicableForFlatFeeForNonHaulageDelivery =
		!allNonConfigurationProductsHaveHaulageDelivery && !isFreeShippingZone
	const isApplicableForFlatFeeForSmallAmount =
		totalGrossCents <=
			flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents &&
		!isApplicableForFlatFeeForNonHaulageDelivery

	const flatFeeForSmallAmount = isApplicableForFlatFeeForSmallAmount
		? flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountCents
		: 0
	const flatFeeForNonHaulageDelivery = isApplicableForFlatFeeForNonHaulageDelivery
		? flatFeesByCurrencyCode[currencyCode].flatFeeForNonHaulageDeliveryCents
		: 0

	if (isFreeShippingZone) {
		return {
			flatFeeForSmallAmount,
			flatFeeForNonHaulageDelivery,
			shippingCostsByLineItemId: {},
		}
	}

	const shippingCostsByLineItemId: ShippingCostsByLineItemId =
		lineItemsWithHaulageDelivery.reduce((shippingCostsByLineItemId, lineItem) => {
			const { id, weightKg = 0, quantity } = lineItem

			const shippingCostsCents = getShippingCostsCents(
				weightKg,
				quantity,
				shippingZoneCosts,
				currencyCode,
			)

			return {
				...shippingCostsByLineItemId,
				[id]: shippingCostsCents,
			}
		}, {})

	return {
		flatFeeForSmallAmount,
		flatFeeForNonHaulageDelivery,
		shippingCostsByLineItemId,
	}
}

export default getCartShippingCosts
